import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { api } from "../lib/api";
import { SectionHeader } from "../components/UI";
import Avatar from "../components/Avatar";
import {
  ArrowLeft, Shield, Download, FileText, Trash2, AlertTriangle,
  Fingerprint, Lock, Eye, Clock, ScrollText,
} from "lucide-react";

/*
 * Settings , Privacy & Data
 * - GDPR data-subject rights (access, export, erasure request)
 * - Retention-window explainer
 * - Cookie/local-storage controls
 */
export default function SettingsPrivacy() {
  const { user, tenant } = useAuth();
  const [stats, setStats] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [erasureModal, setErasureModal] = useState(false);
  const [erasureReason, setErasureReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/analytics/overview").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const exportData = async () => {
    setDownloading(true);
    try {
      // Fetch key user data in parallel
      const [cases, acks, profile] = await Promise.all([
        api.get("/cases").then((r) => r.data).catch(() => []),
        api.get("/my/policy-acknowledgments").then((r) => r.data).catch(() => []),
        api.get("/users/me").then((r) => r.data).catch(() => user),
      ]);
      const payload = {
        exported_at: new Date().toISOString(),
        exported_by: user?.email,
        tenant: tenant?.name,
        profile,
        cases,
        policy_acknowledgments: acks,
      };
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `PropHR-DataExport-${user?.email || "me"}-${Date.now()}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Data export downloaded");
    } catch {
      toast.error("Export failed");
    } finally {
      setDownloading(false);
    }
  };

  const submitErasure = async () => {
    if (!erasureReason.trim()) return toast.error("Please provide a reason");
    setSubmitting(true);
    try {
      await api.post("/tickets", {
        subject: "Data erasure request (GDPR / CCPA)",
        description: `User ${user?.email} is requesting erasure of their personal data.\n\nReason: ${erasureReason}\n\nWorkspace: ${tenant?.name || "n/a"}`,
        category: "Security",
        priority: "high",
      });
      toast.success("Erasure request filed with PropHR compliance");
      setErasureModal(false);
      setErasureReason("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  const clearLocalStorage = () => {
    if (!window.confirm("Clear PropHR local preferences on this device? You will need to re-acknowledge the disclaimer.")) return;
    const authKey = "prophr_token";
    const auth = localStorage.getItem(authKey);
    localStorage.clear();
    if (auth) localStorage.setItem(authKey, auth);
    toast.success("Local preferences cleared");
  };

  return (
    <div className="p-8 max-w-4xl mx-auto" data-testid="settings-privacy">
      <Link to="/settings" className="text-xs text-slate-500 hover:text-slate-900 font-mono flex items-center gap-1 mb-2">
        <ArrowLeft size={12}/> ALL SETTINGS
      </Link>
      <SectionHeader
        title="Privacy & data"
        subtitle="Your data-subject rights, workspace retention, and controls to manage what PropHR knows about you."
      />

      {/* Identity strip */}
      <div className="border border-slate-200 bg-white p-5 mb-6 flex items-center gap-4">
        <Avatar user={user} size={48}/>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-slate-900 truncate">{user?.name}</div>
          <div className="text-xs text-slate-500 truncate">{user?.email}</div>
        </div>
        <div className="text-right hidden md:block">
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Data in this workspace</div>
          <div className="text-sm font-medium text-slate-900">{stats?.total ?? 0} cases</div>
        </div>
      </div>

      {/* Rights grid */}
      <div className="grid md:grid-cols-2 gap-4 mb-8">
        <RightsCard
          Icon={Eye}
          tint="text-blue-700 bg-blue-50 border-blue-200"
          title="Right to access"
          body="See everything PropHR holds about you across cases, policies, and audit trails."
          actionLabel="Download my data (JSON)"
          onAction={exportData}
          loading={downloading}
          testid="privacy-export"
        />
        <RightsCard
          Icon={Download}
          tint="text-emerald-700 bg-emerald-50 border-emerald-200"
          title="Right to portability"
          body="Take your data with you in a machine-readable format."
          actionLabel="Same export applies"
          onAction={exportData}
          loading={downloading}
          testid="privacy-portability"
        />
        <RightsCard
          Icon={FileText}
          tint="text-violet-700 bg-violet-50 border-violet-200"
          title="Right to rectification"
          body="Update your profile, department, or contact info at any time."
          actionLabel="Edit profile"
          to="/profile"
          testid="privacy-rectify"
        />
        <RightsCard
          Icon={Trash2}
          tint="text-rose-700 bg-rose-50 border-rose-200"
          title="Right to erasure"
          body="Request deletion of your personal data (subject to legal & compliance retention)."
          actionLabel="Request erasure"
          onAction={() => setErasureModal(true)}
          testid="privacy-erasure"
        />
      </div>

      {/* Retention explainer */}
      <div className="border border-slate-200 bg-white p-5 mb-4">
        <div className="flex items-center gap-2 mb-3">
          <Clock size={14} strokeWidth={2} className="text-slate-700"/>
          <div className="font-display text-base font-semibold text-slate-900">Retention window</div>
        </div>
        <div className="text-sm text-slate-600 leading-relaxed">
          Your workspace retains data for <span className="font-mono text-slate-900">{tenant?.retention_days ?? 2555} days</span> (~{Math.round((tenant?.retention_days ?? 2555) / 365)} years).
          After this window, cases are purged automatically unless a legal hold applies. This setting is controlled by your Company Admin.
        </div>
      </div>

      {/* Compliance chips */}
      <div className="border border-slate-200 bg-white p-5 mb-4">
        <div className="flex items-center gap-2 mb-3">
          <Shield size={14} strokeWidth={2} className="text-slate-700"/>
          <div className="font-display text-base font-semibold text-slate-900">Compliance frameworks</div>
        </div>
        <div className="flex flex-wrap gap-2">
          {["GDPR", "CCPA", "EEOC", "SOC 2 Type II", "ISO 27001", "HIPAA-ready"].map((f) => (
            <span key={f} className="pill pill-closed inline-flex items-center gap-1">
              <Fingerprint size={10} strokeWidth={2}/> {f}
            </span>
          ))}
        </div>
        <div className="text-xs text-slate-500 mt-3 leading-relaxed">
          Every mutating action , yours or someone else&apos;s , is written to the immutable audit log with a SHA-256 fingerprint.
        </div>
      </div>

      {/* Local data */}
      <div className="border border-slate-200 bg-white p-5">
        <div className="flex items-center gap-2 mb-3">
          <Lock size={14} strokeWidth={2} className="text-slate-700"/>
          <div className="font-display text-base font-semibold text-slate-900">Local device data</div>
        </div>
        <div className="text-sm text-slate-600 leading-relaxed mb-3">
          PropHR keeps a small amount of preference data on this device (notification preferences, disclaimer acknowledgment).
          Clear it any time.
        </div>
        <button
          onClick={clearLocalStorage}
          className="btn-ghost text-xs !border-slate-300"
          data-testid="privacy-clear-local"
        >
          <Trash2 size={12} strokeWidth={2}/> Clear local preferences
        </button>
      </div>

      {/* Erasure modal */}
      {erasureModal && (
        <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white border border-slate-300 max-w-lg w-full p-6 rounded">
            <div className="flex items-start gap-3 mb-3">
              <AlertTriangle size={18} className="text-rose-700 shrink-0 mt-0.5"/>
              <div>
                <h3 className="font-display text-xl font-semibold text-slate-900">Erasure request</h3>
                <p className="text-sm text-slate-500 mt-1">
                  This will open a ticket with PropHR compliance. Note: certain records (audit logs, active investigations, records under legal hold) may be retained per regulatory obligation.
                </p>
              </div>
            </div>
            <label className="eyebrow block mb-1 mt-4">Reason for request</label>
            <textarea
              value={erasureReason}
              onChange={(e) => setErasureReason(e.target.value)}
              rows={4}
              placeholder="Briefly describe your reason , this helps compliance review your request."
              className="input mb-4"
              data-testid="erasure-reason"
            />
            <div className="flex justify-end gap-2">
              <button className="btn-ghost" onClick={() => setErasureModal(false)}>Cancel</button>
              <button className="btn-danger" onClick={submitErasure} disabled={submitting} data-testid="erasure-submit">
                {submitting ? "Submitting…" : "Submit request"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function RightsCard({ Icon, tint, title, body, actionLabel, onAction, to, loading, testid }) {
  return (
    <div className="border border-slate-200 bg-white p-5 flex flex-col">
      <div className="flex items-center gap-3 mb-3">
        <div className={"w-9 h-9 border flex items-center justify-center " + tint}>
          <Icon size={16} strokeWidth={1.75}/>
        </div>
        <div className="font-display text-base font-semibold text-slate-900">{title}</div>
      </div>
      <div className="text-sm text-slate-600 leading-relaxed flex-1">{body}</div>
      {onAction && (
        <button
          onClick={onAction}
          disabled={loading}
          className="mt-4 btn-ghost text-xs self-start !border-slate-300"
          data-testid={testid}
        >
          <ScrollText size={12} strokeWidth={2}/> {loading ? "Preparing…" : actionLabel}
        </button>
      )}
      {to && (
        <Link
          to={to}
          className="mt-4 btn-ghost text-xs self-start !border-slate-300"
          data-testid={testid}
        >
          <ScrollText size={12} strokeWidth={2}/> {actionLabel}
        </Link>
      )}
    </div>
  );
}
